import React from 'react';
import { Calendar, MapPin, TrendingUp, TrendingDown, Minus, Zap, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

const LegislationCard = ({ legislation }) => {
  const isAI = legislation.data_source === 'ai_extracted';

  const getImpactColor = (impactType) => {
    switch (impactType) {
      case 'Liberalizing':
        return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700';
      case 'Restrictive':
        return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700';
      default:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700';
    }
  };

  const getFrictionIcon = (change) => {
    switch (change) {
      case 'Increases':
        return <TrendingUp className="h-4 w-4" />;
      case 'Decreases':
        return <TrendingDown className="h-4 w-4" />;
      default:
        return <Minus className="h-4 w-4" />;
    }
  };

  const formatEventType = (type) =>
    (type || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  return (
    <div
      className={`rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-shadow dark:border-outline-variant/15 dark:bg-surface-container hover:shadow-intel-glow dark:hover:shadow-intel-glow ${
        isAI ? 'ring-1 ring-tertiary/25 dark:bg-surface-container-high/80' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {isAI && <Zap className="mt-0.5 h-5 w-5 shrink-0 text-amber-500 dark:text-tertiary" />}
          <h3 className="line-clamp-2 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">
            {legislation.bill_name}
          </h3>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ml-2 ${getImpactColor(legislation.impact_type)}`}>
          {legislation.impact_type}
        </span>
      </div>

      {isAI && legislation.event_type && (
        <div className="mb-3">
          <span className="rounded-stitch bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-800 dark:bg-tertiary/15 dark:text-tertiary">
            {formatEventType(legislation.event_type)}
          </span>
          {legislation.imposing_country && legislation.target_country && (
            <span className="ml-2 text-xs text-slate-500 dark:text-on-surface-variant">
              {legislation.imposing_country} → {legislation.target_country}
            </span>
          )}
        </div>
      )}
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-slate-600 dark:text-on-surface-variant">
          <MapPin className="mr-2 h-4 w-4" />
          {legislation.country_name || legislation.country_code}
        </div>
        <div className="flex items-center text-sm text-slate-600 dark:text-on-surface-variant">
          <Calendar className="mr-2 h-4 w-4" />
          {isAI ? 'Date' : 'Passed'}: {formatDate(legislation.passed_date)}
        </div>
        {!isAI && legislation.effective_date && (
          <div className="flex items-center text-sm text-slate-600 dark:text-on-surface-variant">
            <Calendar className="mr-2 h-4 w-4" />
            Effective: {formatDate(legislation.effective_date)}
          </div>
        )}
        {isAI && legislation.tariff_rate_percent != null && (
          <div className="text-sm">
            <span className="font-medium text-amber-700 dark:text-primary-fixed-dim">
              {legislation.tariff_rate_percent}% tariff rate
            </span>
          </div>
        )}
      </div>

      {legislation.summary && (
        <p className="mb-4 line-clamp-3 text-sm text-slate-700 dark:text-on-surface-variant">{legislation.summary}</p>
      )}

      <div className="mt-4 flex items-center justify-between rounded-stitch-lg bg-slate-50 px-3 py-3 dark:bg-surface-container-lowest">
        <div className="flex items-center text-sm">
          {getFrictionIcon(legislation.trade_friction_change)}
          <span className="ml-2 text-slate-600 dark:text-on-surface-variant">
            Trade Friction: <span className="font-medium">{legislation.trade_friction_change}</span>
          </span>
        </div>
        {legislation.affected_sectors && legislation.affected_sectors.filter(Boolean).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {legislation.affected_sectors.filter(Boolean).slice(0, 3).map((sector, idx) => (
              <span
                key={idx}
                className="rounded-stitch bg-slate-200/80 px-2 py-1 text-xs text-slate-700 dark:bg-secondary-container dark:text-on-secondary-container"
              >
                {sector}
              </span>
            ))}
          </div>
        )}
      </div>

      {isAI && (
        <div className="mt-3 flex items-center justify-between rounded-stitch-lg bg-slate-50 px-3 py-3 dark:bg-surface-container-lowest">
          <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-on-surface-variant">
            {legislation.impact_score != null && (
              <span className={`px-2 py-0.5 rounded font-medium ${
                legislation.impact_score >= 60 ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                legislation.impact_score >= 40 ? 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400' :
                'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
              }`}>
                Impact: {legislation.impact_score}
              </span>
            )}
            {legislation.confidence_score != null && (
              <span>Confidence: {(parseFloat(legislation.confidence_score) * 100).toFixed(0)}%</span>
            )}
          </div>
          {legislation.source_url && (
            <a href={legislation.source_url} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-1 text-xs text-primary-600 hover:underline dark:text-primary">
              Source <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default LegislationCard;
